import React, { useState, useRef, useEffect } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AppBar,
  Toolbar,
  IconButton,
  Box,
  Typography,
  Badge,
  Avatar,
  ButtonBase,
  Menu,
  MenuItem,
  Divider,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { MenuIcon, HelpOutline, BellIcon, ChevronDown } from '../ui/Icons';
import { useAuth } from '../../context/AuthContext';
import LanguageSwitcher from '../LanguageSwitcher';

/**
 * White app bar shown above content in the back-office shell. Houses the
 * sidebar toggle, brand-agnostic help link, language switcher, notifications,
 * and a user pill that opens a profile/logout menu.
 *
 * Reads role for the role-tag under the user name. Designed to be embedded
 * inside <AppShell>; do not place outside it.
 */
const TopBar = ({
  onMenuClick,
  notificationCount = 0,
  rightSlot,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const upMd = useMediaQuery(theme.breakpoints.up('md'));
  const { user, logout, isAdmin } = useAuth();
  const [menuAnchor, setMenuAnchor] = useState(null);
  const buttonRef = useRef(null);

  const handleLogout = async () => {
    setMenuAnchor(null);
    await logout();
    navigate('/login');
  };

  // Role label
  const roleLabel = isAdmin?.() ? t('roles.admin', 'Super Admin') : t('roles.manager', 'Manager');

  // Initials for avatar fallback
  const initials = (user?.name || user?.email || '?')
    .split(/\s+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <AppBar
      position="sticky"
      sx={{
        // The TopBar sits inside the main column in AppShell, which is already
        // offset by the permanent sidebar at md+. So no extra inset needed here.
        width: '100%',
      }}
    >
      <Toolbar sx={{ gap: 1.5, minHeight: { xs: 64, md: 72 }, px: { xs: 2, md: 3 } }}>
        <IconButton
          onClick={onMenuClick}
          edge="start"
          size="medium"
          aria-label={t('topbar.toggleNav', 'Toggle navigation')}
          sx={{ color: 'text.primary' }}
        >
          <MenuIcon size={22} />
        </IconButton>

        <Box sx={{ flex: 1 }} />

        {rightSlot}

        <ButtonBase
          sx={{
            display: { xs: 'none', sm: 'inline-flex' },
            alignItems: 'center',
            gap: 0.75,
            px: 1.5,
            py: 1,
            borderRadius: 2,
            color: 'text.secondary',
            '&:hover': { backgroundColor: 'action.hover' },
          }}
          aria-label={t('topbar.needHelp', 'Need help?')}
        >
          <HelpOutline size={18} />
          {upMd && <Typography variant="body2" sx={{ fontWeight: 500 }}>{t('topbar.needHelp', 'Need help?')}</Typography>}
        </ButtonBase>

        <Box sx={{ display: 'inline-flex' }}>
          <LanguageSwitcher />
        </Box>

        <IconButton
          aria-label={t('topbar.notifications', 'Notifications')}
          sx={{ color: 'text.primary' }}
        >
          <Badge color="error" badgeContent={notificationCount} max={9} invisible={!notificationCount}>
            <BellIcon size={22} />
          </Badge>
        </IconButton>

        <ButtonBase
          ref={buttonRef}
          onClick={() => setMenuAnchor(buttonRef.current)}
          aria-haspopup="true"
          aria-expanded={Boolean(menuAnchor)}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1.25,
            padding: 0.5,
            paddingInlineEnd: 1.5,
            borderRadius: 9999,
            '&:hover': { backgroundColor: 'action.hover' },
          }}
        >
          <Avatar
            alt={user?.name || user?.email || ''}
            src={user?.avatar_url || undefined}
            sx={{ width: 36, height: 36, fontSize: 14, fontWeight: 700, bgcolor: 'primary.light' }}
          >
            {initials}
          </Avatar>
          {upMd && (
            <Box sx={{ textAlign: 'start' }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', lineHeight: 1.2 }}>
                {user?.name || user?.email}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {roleLabel}
              </Typography>
            </Box>
          )}
          {upMd && <ChevronDown size={16} style={{ color: '#64748B' }} />}
        </ButtonBase>

        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem component={RouterLink} to="/profile" onClick={() => setMenuAnchor(null)}>
            {t('header.profile', 'My profile')}
          </MenuItem>
          <MenuItem component={RouterLink} to="/subscription" onClick={() => setMenuAnchor(null)}>
            {t('header.subscription', 'Subscription')}
          </MenuItem>
          <Divider />
          <MenuItem onClick={handleLogout}>{t('header.logout', 'Sign out')}</MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

export default TopBar;
